import React from 'react';
import Icon from '../../../components/AppIcon';

const ProblemSection = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  const daysUntilEndOfYear = () => {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31); // December 31
    const diff = endOfYear - now;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const daysLeft = daysUntilEndOfYear();

  return (
    <section className="py-0 bg-white">
      {/* Urgent alert banner — full width */}
      <div className="bg-amber-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-3 flex-wrap text-center">
          <Icon name="AlertTriangle" size={18} className="flex-shrink-0" />
          <span className="font-heading font-bold text-sm sm:text-base tracking-wide uppercase">
            Urgente wijziging — Salderingsregeling stopt per 1 januari 2027
          </span>
          <span className="hidden sm:inline text-amber-100 text-sm">|</span>
          <span className="text-amber-100 text-sm font-body">Zonnepaneelhouders verliezen tot €500/jaar zonder actie</span>
        </div>
      </div>

      <div className="py-16 lg:py-24 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section header */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="inline-flex items-center gap-2 bg-amber-100 border border-amber-300 text-amber-800 px-4 py-2 rounded-full text-xs font-heading font-bold uppercase tracking-widest mb-5">
              <Icon name="Clock" size={13} />
              <span>Nog maar {daysLeft} dagen</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground leading-tight mb-4">
              Vanaf 1 januari 2027 verandert<br className="hidden sm:block" /> de rekensom van je zonnepanelen
            </h2>
            <p className="text-lg font-body text-muted-foreground leading-relaxed">
              Je wekt overdag stroom op die je 's avonds nodig hebt. Zonder opslag verkoop je 'm goedkoop terug en koop je 'm duur in — dat kost je honderden euro's per jaar.
            </p>
          </div>

          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

            {/* Left — calculation card (PROMINENT) */}
            <div className="order-1 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl border border-amber-200">
                {/* Card header */}
                <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Calculator" size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-heading font-bold text-base leading-tight">Rekenvoorbeeld — 10 zonnepanelen</p>
                    <p className="text-amber-100 text-xs font-body">±2.800 kWh teruglevering per jaar · tarief €0,27/kWh</p>
                  </div>
                </div>

                <div className="bg-white p-6 space-y-4">
                  {/* Before 2027 */}
                  <div className="rounded-xl border-2 border-green-200 bg-green-50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-heading font-bold text-green-700 uppercase tracking-wider">Situatie vóór 2027</span>
                      <span className="text-xs font-heading font-bold text-white bg-green-500 px-2.5 py-1 rounded-full">Nu</span>
                    </div>
                    <div className="space-y-1.5 mb-3">
                      <div className="flex justify-between text-sm font-body text-foreground">
                        <span>Teruglevering</span>
                        <span className="font-semibold">2.800 kWh/jaar</span>
                      </div>
                      <div className="flex justify-between text-sm font-body text-foreground">
                        <span>Vergoeding (volledig gesaldeerd)</span>
                        <span className="font-semibold">€0,27/kWh</span>
                      </div>
                    </div>
                    <div className="border-t border-green-200 pt-3 flex items-center justify-between">
                      <span className="text-sm font-heading font-semibold text-green-800">Jaarlijkse verrekening</span>
                      <span className="text-2xl font-heading font-extrabold text-green-600">+ €756</span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <div className="flex-1 h-px bg-border" />
                    <div className="flex items-center gap-1.5 bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
                      <Icon name="ArrowDown" size={13} className="text-amber-700" />
                      <span className="text-xs font-heading font-bold text-amber-800 uppercase tracking-wide">Wijziging 2027</span>
                    </div>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* After 2027 */}
                  <div className="rounded-xl border-2 border-red-200 bg-red-50 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-heading font-bold text-red-700 uppercase tracking-wider">Situatie ná 1 jan 2027</span>
                      <span className="text-xs font-heading font-bold text-white bg-red-500 px-2.5 py-1 rounded-full">Wijziging</span>
                    </div>
                    <div className="space-y-1.5 mb-3">
                      <div className="flex justify-between text-sm font-body text-foreground">
                        <span>Teruglevering</span>
                        <span className="font-semibold">2.800 kWh/jaar</span>
                      </div>
                      <div className="flex justify-between text-sm font-body text-foreground">
                        <span>Vergoeding (min. 50% van tarief)</span>
                        <span className="font-semibold text-red-600">≤ €0,135/kWh</span>
                      </div>
                    </div>
                    <div className="border-t border-red-200 pt-3 flex items-center justify-between">
                      <span className="text-sm font-heading font-semibold text-red-800">Jaarlijkse verrekening</span>
                      <span className="text-2xl font-heading font-extrabold text-red-600">+ €378</span>
                    </div>
                  </div>

                  {/* Net loss highlight */}
                  <div className="rounded-xl bg-gray-900 text-white p-5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-heading font-bold uppercase tracking-wider text-gray-400 mb-1">Jaarlijks verlies zonder batterij</p>
                      <p className="text-sm font-body text-gray-300">Verschil in vergoeding per jaar</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-3xl font-heading font-extrabold text-red-400">− €378</span>
                      <p className="text-xs text-gray-400 mt-0.5">per jaar</p>
                    </div>
                  </div>

                  {/* Solution */}
                  <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Battery" size={15} className="text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-heading font-bold text-foreground mb-0.5">Met een thuisbatterij</p>
                      <p className="text-sm font-body text-muted-foreground">Gebruik je die stroom gewoon zelf — geen verlies, geen verrassing op je energierekening.</p>
                    </div>
                  </div>

                  <p className="text-[10px] font-caption text-muted-foreground">
                    * Indicatief rekenvoorbeeld op basis van 10 panelen (±2.800 kWh/jaar), leveringstarief €0,27/kWh. Werkelijke bedragen afhangen van actuele tarieven en verbruik.
                  </p>
                </div>
              </div>
            </div>

            {/* Right — explanation + key figures */}
            <div className="order-2 lg:order-2 space-y-6">
              {/* Key figures */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border border-border rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-heading font-extrabold text-red-500 leading-none mb-1">50%</p>
                  <p className="text-xs font-caption text-muted-foreground leading-tight">Min. korting op teruglevering</p>
                </div>
                <div className="bg-white border border-border rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-heading font-extrabold text-amber-500 leading-none mb-1">2027</p>
                  <p className="text-xs font-caption text-muted-foreground leading-tight">Ingangsdatum wijziging</p>
                </div>
                <div className="bg-white border border-border rounded-xl p-4 text-center shadow-sm">
                  <p className="text-2xl font-heading font-extrabold text-foreground leading-none mb-1">€378</p>
                  <p className="text-xs font-caption text-muted-foreground leading-tight">Verlies per jaar (10 panelen)</p>
                </div>
              </div>

              {/* Explanation */}
              <div className="space-y-4">
                <p className="font-body text-foreground leading-relaxed">
                  De salderingsregeling stopt definitief op <strong>1 januari 2027</strong>. Teruggeleverde zonnestroom mag dan niet meer worden weggestreept tegen afgenomen stroom. De vergoeding daalt naar minimaal <strong>50% van het kale leveringstarief</strong>.
                </p>
                <p className="font-body text-foreground leading-relaxed">
                  Dat klinkt technisch, maar de impact is concreet: wie nu niets doet, betaalt straks meer voor dezelfde stroom die hij zelf opwekt.
                </p>
              </div>

              {/* Without panels */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Info" size={15} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-heading font-semibold text-foreground mb-1">Geen zonnepanelen?</p>
                    <p className="text-sm font-body text-muted-foreground leading-relaxed">
                      Een thuisbatterij is ook interessant zonder panelen. Met een dynamisch energiecontract laad je goedkoop op tijdens daluren en vermijd je dure piekverbruik.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="pt-2">
                <button
                  onClick={() => scrollTo('formulier')}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-primary text-primary-foreground font-heading font-bold text-sm rounded-lg hover:bg-primary/90 transition-colors shadow-md"
                >
                  Bespreek mijn situatie gratis
                  <Icon name="ArrowRight" size={16} className="ml-2" />
                </button>
                <p className="text-xs font-caption text-muted-foreground mt-2">Vrijblijvend adviesgesprek · Geen verplichtingen</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
